/**
 * THE single source of truth for every texture & audio key in the game.
 *
 * Placeholder art (generated at runtime) and final art (loaded files) share
 * these exact keys — so swapping in real assets is a file drop + a manifest
 * line, never an engine-code change. See registry/textures.ts and
 * registry/assetManifest.ts.
 */

export const TX = {
  // characters
  player: 'tx_player',
  playerAction: 'tx_player_action',
  pet: 'tx_pet',
  petCat: 'tx_pet_cat',
  petDog: 'tx_pet_dog',
  clara: 'tx_clara',
  // structures / objects
  house: 'tx_house',
  house2: 'tx_house2',
  house3: 'tx_house3',
  cabin: 'tx_cabin',
  estate: 'tx_estate',
  castle: 'tx_castle',
  blueprint: 'tx_blueprint',
  bed: 'tx_bed',
  shippingBin: 'tx_shipping_bin',
  sign: 'tx_sign',
  // ui / fx
  heart: 'tx_heart',
  coin: 'tx_coin',
  energyPip: 'tx_energy_pip',
  moveMarker: 'tx_move_marker',
  uiSlot: 'tx_ui_slot',
  uiSlotActive: 'tx_ui_slot_active',
  // tools (palette)
  toolHand: 'tx_tool_hand',
  toolHoe: 'tx_tool_hoe',
  toolWater: 'tx_tool_water',
  toolAxe: 'tx_tool_axe',
  toolScythe: 'tx_tool_scythe',
  // tier-2 world objects
  weed: 'tx_weed',
  shop: 'tx_shop',
  toolMove: 'tx_tool_move',
  // ambient life
  cloud: 'tx_cloud',
  bird: 'tx_bird',
  smoke: 'tx_smoke',
  ray: 'tx_ray',
  raindrop: 'tx_raindrop',
  snowflake: 'tx_snowflake',
  leaf: 'tx_leaf',
  dust: 'tx_dust',
  sparkle: 'tx_sparkle',
  firefly: 'tx_firefly',
  shootingstar: 'tx_shootingstar',
  moon: 'tx_moon',
  bunting: 'tx_bunting',
  // forest / foraging
  trailArch: 'tx_trail_arch',
  forestGround: 'tx_forest_ground',
  townGround: 'tx_town_ground',
  townPath: 'tx_town_path',
  well: 'tx_well',
  booth: 'tx_booth',
  mole: 'tx_mole',
  vendorCart: 'tx_vendor_cart',
  npcA: 'tx_npc_a',
  npcB: 'tx_npc_b',
  npcC: 'tx_npc_c',
  forestTree: 'tx_forest_tree',
  treefort: 'tx_treefort',
  berryBush: 'tx_berry_bush',
  forageMushroom: 'tx_forage_mushroom',
  herbPlant: 'tx_herb_plant',
  stump: 'tx_stump',
  // beach
  sand: 'tx_sand',
  ocean: 'tx_ocean',
  shell: 'tx_shell',
  starfish: 'tx_starfish',
  seaweed: 'tx_seaweed',
  butterfly: 'tx_butterfly',
  // mine
  mineFloor: 'tx_mine_floor',
  mineRock: 'tx_mine_rock',
  oreRock: 'tx_ore_rock',
  ladder: 'tx_ladder',
  photo: 'tx_photo',
  // animals
  chicken: 'tx_chicken',
  barn: 'tx_barn',
  cow: 'tx_cow',
  sheep: 'tx_sheep',
  pig: 'tx_pig',
  duck: 'tx_duck',
  egg: 'tx_egg',
  // fishing
  toolRod: 'tx_tool_rod',
  bobber: 'tx_bobber',
  // house interior
  intFloor: 'tx_int_floor',
  intWall: 'tx_int_wall',
  intDoor: 'tx_int_door',
  chest: 'tx_chest',
  workbench: 'tx_workbench',
  stove: 'tx_stove',
  mailbox: 'tx_mailbox',
  mailFlag: 'tx_mail_flag',
  // outfits (hats)
  hatStraw: 'tx_hat_straw',
  hatBow: 'tx_hat_bow',
  hatParty: 'tx_hat_party',
  hatCrown: 'tx_hat_crown',
} as const;

export type TextureKey = string;

/** The packed terrain tileset texture used by the tilemap. */
export const TILESET_KEY = 'tx_tileset';
export const TILESET_NAME = 'terrain'; // must match the tileset "name" in the Tiled JSON

/**
 * Fixed tile order inside the generated tileset strip.
 * Tiled gid = index + 1 (gid 0 = empty). KEEP IN SYNC with scripts/gen-map.mjs.
 */
export const TILE_INDEX = {
  grass: 0,
  grassAlt: 1,
  soilDry: 2,
  soilWet: 3,
  water: 4,
  path: 5,
  fence: 6,
  tree: 7,
  flowers: 8,
  plank: 9,
} as const;
export const TILESET_COLUMNS = 10;

/** Tileset indices (0-based) whose tiles collide. */
export const COLLIDING_TILE_INDICES = [TILE_INDEX.water, TILE_INDEX.fence, TILE_INDEX.tree];

/** Crop stage texture key (one texture per growth stage). */
export const cropTextureKey = (cropId: string, stage: number): string => `tx_crop_${cropId}_${stage}`;

/** Inventory icon key for any item id. */
export const itemIconKey = (itemId: string): string => `tx_icon_${itemId}`;

export const AUDIO = {
  music: 'au_music',
  till: 'au_till',
  plant: 'au_plant',
  water: 'au_water',
  harvest: 'au_harvest',
  coin: 'au_coin',
  pet: 'au_pet',
  pop: 'au_pop',
  baby: 'au_baby',
  babble: 'au_babble',
  moo: 'au_moo',
  baa: 'au_baa',
  oink: 'au_oink',
  quack: 'au_quack',
  chop: 'au_chop',
  weed: 'au_weed',
  chirp: 'au_chirp',
  splash: 'au_splash',
  meow: 'au_meow',
  bark: 'au_bark',
  yawn: 'au_yawn',
  twinkle: 'au_twinkle',
  cricket: 'au_cricket',
  drip: 'au_drip',
  click: 'au_click',
} as const;

export type AudioKey = (typeof AUDIO)[keyof typeof AUDIO];
